import {
    Box,
    Button,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React, {useState} from 'react'
import ProductTileImage from '../../product-tile/partials/product-tile-image'
import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'

import useProductData from '../../../hooks/use-product-data'
import {PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'
import {FormattedMessage, useIntl} from 'react-intl'

import DisplayPrice from '../../display-price'
import OmnibusPrice from '../../display-price/omnibus-price'
import {getPriceData, getBundleId} from '../../../utils/product-utils'

import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import Link from '@salesforce/retail-react-app/app/components/link'

import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../../custom-api/layers/default'

import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useAddToCartModalContext} from '../../../hooks/use-add-to-cart-modal'
import {useToast} from '../../../hooks/use-toast'
import {API_ERROR_MESSAGE} from '../../../constants'

const ProductTileRecommended = ({
    variant,
    tileImageVariant,
    product,
    badgeDetails,
    productIndex = undefined,
    style,
    prefix = 'default',
    isDragging = false,
    onDiscoverMoreClick = noop,
}) => {
    const styles = useMultiStyleConfig('RecommendedProducts', {variant})
    const {currency} = useCurrency()
    const {addLayer} = useGoogleTagManager()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const {formatMessage} = useIntl()
    const showToast = useToast()
    const [isAddingToCart, setIsAddingToCart] = useState(false)
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {onOpen: onAddToCartModalOpen} = useAddToCartModalContext()

    const {productUrl, priceData} = useProductData(product, PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID)
    const isFreeGift =
        product?.priceAfterItemDiscount === 0 || product?.priceAfterOrderDiscount === 0
    const resolvedVariant = product?.c_variants?.[0] || product?.variants?.[0]
    const productName = product?.c_defaultName || product?.name || product?.productName
    const isFrequentlyBoughtTogether = prefix === 'frequently-bought-together'

    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error',
        })
    }

    const selectItemTileEventGtm = async () => {
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'ga_event',
                event_name: 'select_item',
                item_list_name: 'carousel_pdp',
                bundle: product?.c_isBundle,
                bundle_id: getBundleId(product),
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.c_variants?.[0]?.productId,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.id,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        // get_reevoo_rating() || get_bv_rating(),
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdha_category,
                        index: productIndex + 1,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    const addToCartEventGtm = async () => {
        if (typeof addLayer !== 'function') {
            return
        }

        await addLayer(polish_ecommerce_gtm_event.bind(this))
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'ga_event',
                event_name: 'add_to_cart',
                currency,
                bundle: product?.c_isBundle,
                bundle_id: getBundleId(product),
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: resolvedVariant?.productId,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.id,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdha_category,
                        quantity: 1,
                        index: productIndex + 1,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    const handleAddToCartClick = async () => {
        if (!resolvedVariant?.productId || isAddingToCart) {
            return
        }

        setIsAddingToCart(true)

        try {
            await addItemToNewOrExistingBasket([
                {
                    productId: resolvedVariant.productId,
                    price: resolvedVariant?.price ?? product?.price,
                    quantity: 1,
                    c_MaterialGroup: product?.c_materialGroup,
                    c_categoryName:
                        product?.c_productCategory ||
                        product?.c_defaultCategoryName ||
                        product?.c_categoryName,
                    c_subModelIdentifier: product?.c_subModelIdentifier,
                    c_prdhaCategory: product?.c_prdhaCategory || product?.c_prdha_category,
                },
            ])

            onAddToCartModalOpen({
                itemsAdded: [
                    {
                        product,
                        variant: resolvedVariant,
                        quantity: 1,
                    },
                ],
                selectedQuantity: 1,
                isBundle: false,
            })
        } catch (error) {
            console.error('error', error)
            showError()
            setIsAddingToCart(false)
            return
        }

        try {
            await addToCartEventGtm()
        } catch (error) {
            console.error('Failed to send recommended product add_to_cart GTM event', error)
        }

        setIsAddingToCart(false)
    }

    return (
        <Flex
            {...styles.recommendedProductCard}
            className={`recommended-products-pruduct-tile-${prefix}`}
            style={style}
        >
            <ProductTileImage
                badgeDetails={badgeDetails}
                product={product}
                enableFavourite
                productUrl={productUrl}
                variant={tileImageVariant}
                priceData={priceData}
                isFreeGift={isFreeGift}
                selectItemTileEventGtm={selectItemTileEventGtm}
                isDragging={isDragging}
                height="100%"
                imageStyle={{...styles.imageStyle}}
            />
            <Flex {...styles.productDetailsContainer}>
                <Text {...styles.productDetailId}>
                    {product?.c_subModelIdentifier || product?.id}
                </Text>
                <Box>
                    <Text {...styles.productDetailName}>
                        {product?.c_variantIdentifier || product?.name || product?.productName}
                    </Text>
                </Box>
                <Flex flexDirection={'column'}>
                    <DisplayPrice
                        priceData={priceData}
                        currency={currency}
                        listPriceProps={{...styles.productListPrice}}
                        currentPriceProps={{...styles.productDetailPrice}}
                        discountProps={{display: 'none'}}
                        productDetailPriceView={true}
                        hideListingPrice={true}
                        notShoppableCountry={notShoppableCountry}
                    />
                    <Box {...styles.productOmnibusWrapper}>
                        {!notShoppableCountry && priceData.showOmnibusPriceDiscount && (
                            <OmnibusPrice
                                priceData={priceData}
                                currency={currency}
                                omnibusExpandedView={true}
                                variant="recommended"
                            />
                        )}
                    </Box>
                </Flex>
            </Flex>
            <Flex>
                {isFrequentlyBoughtTogether ? (
                    <Button
                        variant="primary"
                        w="100%"
                        px={0}
                        onClick={handleAddToCartClick}
                        isLoading={isAddingToCart}
                        {...styles.discoverMoreButton}
                        data-testid="button"
                        aria-label={formatMessage(
                            {
                                defaultMessage: 'Add {productName} to cart',
                                id: 'recommended_product_card.add_to_cart_button.aria_label',
                            },
                            {productName},
                        )}
                    >
                        <FormattedMessage defaultMessage="Add to Cart" id="global.add-to-cart" />
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        as={Link}
                        to={productUrl}
                        w="100%"
                        px={0}
                        onClick={(e) => {
                            onDiscoverMoreClick(product, e)
                            selectItemTileEventGtm()
                        }}
                        {...styles.discoverMoreButton}
                        data-testid="button"
                        aria-label={formatMessage(
                            {
                                defaultMessage: 'Discover More about {productName}',
                                id: 'recommended_product_card.discover_more_button.aria_label',
                            },
                            {productName},
                        )}
                    >
                        <FormattedMessage
                            defaultMessage="Discover More"
                            id="recommended_product_card.discover_more_button"
                        />
                    </Button>
                )}
            </Flex>
        </Flex>
    )
}

ProductTileRecommended.propTypes = {
    variant: PropTypes.string,
    tileImageVariant: PropTypes.string,
    product: PropTypes.object,
    badgeDetails: PropTypes.array,
    prefix: PropTypes.string,
    productIndex: PropTypes.number,
    style: PropTypes.object,
    isDragging: PropTypes.bool,
    onDiscoverMoreClick: PropTypes.func,
}

export default ProductTileRecommended
